import { type ComponentProps, type ReactNode, useId, useState } from 'react'

import { controlClass } from '@/components/control-class'
import { FieldContext, useControl } from '@/components/field-control'
import { EyeIcon, EyeOffIcon } from '@/components/icons'

import { cx } from '@/lib/cx'
import { useErrorMessage } from '@/lib/i18n/errors'
import { useT } from '@/lib/i18n/translate'

export interface FieldProps {
  label: ReactNode
  hint?: ReactNode
  /** A string shows verbatim (client validation); anything else renders as an `ApiError`. */
  error?: unknown
  children: ReactNode
  className?: string
}

/** Top margin that drops a control beside a `Field` to its input's row: the label's line height plus the gap. */
export const FIELD_LABEL_OFFSET = 'mt-6.5'

/** Label + control + hint/error, wired with ids so screen readers announce all of it. */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId()
  const errorMessage = useErrorMessage()
  const message = error == null || error === '' ? undefined : typeof error === 'string' ? error : errorMessage(error)
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = message ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: message !== undefined }}>
      <div className={cx('flex flex-col gap-1.5', className)}>
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {children}
        {message && (
          <p id={errorId} role="alert" className="text-danger text-sm">
            {message}
          </p>
        )}
        {hint && (
          <p id={hintId} className="text-muted text-sm">
            {hint}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  )
}

const control = cx(controlClass, 'w-full')

export function Input({ className, ...rest }: ComponentProps<'input'>) {
  return <input {...useControl()} className={cx(control, 'h-tap', className)} {...rest} />
}

/** Password field with a show/hide toggle; the toggle is a real button so it is reachable by keyboard. */
export function PasswordInput({ className, ...rest }: Omit<ComponentProps<'input'>, 'type'>) {
  const t = useT()
  const [shown, setShown] = useState(false)
  return (
    <div className="relative">
      <Input type={shown ? 'text' : 'password'} className={cx('pr-11', className)} {...rest} />
      <button
        type="button"
        aria-pressed={shown}
        aria-label={t('auth.showPassword')}
        onClick={() => setShown((s) => !s)}
        className="text-muted hover:text-fg absolute inset-y-0 right-0 flex w-11 items-center justify-center"
      >
        {shown ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  )
}

export function Textarea({ className, rows = 3, ...rest }: ComponentProps<'textarea'>) {
  return <textarea {...useControl()} rows={rows} className={cx(control, 'py-2', className)} {...rest} />
}

export function Select({ className, ...rest }: ComponentProps<'select'>) {
  return <select {...useControl()} className={cx(control, 'h-tap', className)} {...rest} />
}

export interface CheckboxProps extends Omit<ComponentProps<'input'>, 'type'> {
  label: ReactNode
}

/** Self-labeled; place directly, not inside a `Field`. */
export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={cx('flex min-h-tap cursor-pointer items-center gap-3 text-base', className)}>
      <input type="checkbox" className="accent-accent size-5 shrink-0" {...rest} />
      {label}
    </label>
  )
}
